import type { ComponentType, ReactNode, SVGAttributes } from "react";
import { Box, Column, Row, TextWithTooltip } from "@hightouch/htui";

type NameCellProps = {
  /** An `IconTile`, or a thumbnail of the same size. */
  leading: ReactNode;
  title: string;
  /** Small badges shown after the title, e.g. "Draft" or "Synced". */
  badges?: ReactNode;
  description?: string | null;
};

/** The Knowledge lists' Name column: a tile, then the title over a one-line description. */
export const NameCell = ({
  leading,
  title,
  badges,
  description,
}: NameCellProps) => (
  <Row align="center" gap={3} minWidth={0}>
    <Box flexShrink={0}>{leading}</Box>
    <Column minWidth={0} flex={1}>
      <Row align="center" gap={2} minWidth={0}>
        <TextWithTooltip fontWeight="medium">{title}</TextWithTooltip>
        {badges && (
          <Row align="center" gap={1} flexShrink={0}>
            {badges}
          </Row>
        )}
      </Row>
      {description && (
        <TextWithTooltip size="sm" color="text.secondary">
          {description}
        </TextWithTooltip>
      )}
    </Column>
  </Row>
);

const ICON_TILE_TONES = {
  neutral: {
    gradient: "linear(to-b, gray.200, base.background)",
    icon: "text.secondary",
  },
  success: {
    gradient: "linear(to-b, success.backgroundHover, success.background)",
    icon: "green.500",
  },
  warning: {
    gradient: "linear(to-b, warning.200, warning.background)",
    icon: "icon.warning",
  },
} as const;

export type IconTileTone = keyof typeof ICON_TILE_TONES;

export const IconTile = ({
  icon,
  size = "md",
  tone = "neutral",
}: {
  icon: ComponentType<SVGAttributes<SVGElement>>;
  /** "sm" is the detail rail's row tile; "md" is the list's Name column tile. */
  size?: "sm" | "md";
  /** A status tint, e.g. success for a verified definition. */
  tone?: IconTileTone;
}) => (
  <Row
    align="center"
    justify="center"
    boxSize={size === "sm" ? 8 : 10}
    borderRadius={size === "sm" ? "md" : "lg"}
    border="2px solid"
    borderColor="white"
    bgGradient={ICON_TILE_TONES[tone].gradient}
    boxShadow="sm"
    color={ICON_TILE_TONES[tone].icon}
  >
    <Box as={icon} boxSize={size === "sm" ? 3 : 4} />
  </Row>
);
